/**
 * scheduleMasterGantt.ts
 * Frontend service for the Master Gantt API.
 *
 * Phase 4, Item 1 — thin API wrapper over
 *   GET /api/schedule/master-gantt?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 *
 * All API calls go through apiClient (MSAL bearer + base URL).
 */

import apiClient from './apiClient';
import type { MasterGanttResponse } from '../types/schedule.js';
import { computeDefaultRange } from '../pages/schedule/views/dateRangePresets.js';

/**
 * Fetch hierarchical Project → WorkOrder → Bookings + Assignments data.
 *
 * @param startDate  ISO date 'YYYY-MM-DD' (default: today − 7 days on server)
 * @param endDate    ISO date 'YYYY-MM-DD' (default: today + 90 days on server)
 * @param quoteKey   Optional UUID. When provided, scopes the response to only
 *                   that quote's tentative bookings (Phase 5 Item 5 — Preview
 *                   Schedule modal). The server skips WorkOrder and Manual-event
 *                   buckets and returns only Bucket B filtered to this quote.
 */
export async function fetchMasterGanttData(
  startDate: string,
  endDate: string,
  quoteKey?: string,
): Promise<MasterGanttResponse> {
  const params = new URLSearchParams({ startDate, endDate });
  if (quoteKey) params.set('quoteKey', quoteKey);
  const response = await apiClient.get<MasterGanttResponse>(
    `/schedule/master-gantt?${params.toString()}`,
  );
  return response.data;
}

/**
 * Compute the default date range for the Master Gantt.
 *
 * BUG-2026-0194 — the lab asked for the Master Gantt to open on "Next 3
 * months" (the BUG-2026-0077 one-week default was too narrow; the submitter's
 * first action was always widening it). The window is DERIVED from the shared
 * quick-range preset (same as the Machine Schedule since BUG-2026-0195), so the
 * toolbar's Quick-range dropdown labels the fresh view "Next 3 months" via
 * `matchRangePreset` instead of "Custom range".
 */
export function defaultMasterGanttRange(): { startDate: string; endDate: string } {
  return computeDefaultRange(new Date());
}
